/**
 * Minimal Call UI Component
 *
 * Provides basic calling functionality:
 * - Phone number input
 * - Call/Hangup button
 * - Call status display
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Phone, PhoneOff, Grid3X3 } from 'lucide-react';
import { toast } from 'sonner';
import { TelnyxClient, CallState } from '@/lib/telnyx/client';
import * as api from '@/lib/api/client';
import { CallOutcomeModal } from './CallOutcomeModal';
import { useUpdateCallOutcome } from '@/lib/api/hooks/useCalls';

interface CallUIProps {
  prefilledPhone?: string;
  prospectId?: string;
  prospectName?: string;
}

export const CallUI = ({ prefilledPhone, prospectId, prospectName }: CallUIProps = {}) => {
  const [phoneNumber, setPhoneNumber] = useState(prefilledPhone || '');
  const [callState, setCallState] = useState<CallState>('idle');
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentCallId, setCurrentCallId] = useState<string | null>(null);
  const [fromNumberDisplay, setFromNumberDisplay] = useState<string | null>(null);
  const [currentCall, setCurrentCall] = useState<any | null>(null);
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string>('');
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string>('');
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [completedCallId, setCompletedCallId] = useState<string | null>(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const [dtmfDigits, setDtmfDigits] = useState('');

  const [isTranscribing, setIsTranscribing] = useState(false);

  const telnyxClientRef = useRef<TelnyxClient | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const localAudioRef = useRef<HTMLAudioElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const pendingBlobRef = useRef<Blob | null>(null);

  const updateCallOutcome = useUpdateCallOutcome();

  // Update phone number when prefilled prop changes
  useEffect(() => {
    if (prefilledPhone) {
      setPhoneNumber(prefilledPhone);
    }
  }, [prefilledPhone]);

  // Enumerate audio devices on mount and load saved preference
  useEffect(() => {
    const enumerateDevices = async () => {
      try {
        // Request microphone permission first
        await navigator.mediaDevices.getUserMedia({ audio: true });

        // Now enumerate devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
        setAudioInputDevices(audioInputs);
        setAudioOutputDevices(audioOutputs);

        console.log('[AUDIO] Available microphones:', audioInputs);
        console.log('[AUDIO] Available speakers:', audioOutputs);

        // Load saved microphone preference or use default
        const savedMicId = localStorage.getItem('preferred_microphone');
        if (savedMicId && audioInputs.find(d => d.deviceId === savedMicId)) {
          setSelectedMicId(savedMicId);
          console.log('[AUDIO] Loaded saved microphone preference:', savedMicId);
        } else if (audioInputs.length > 0) {
          setSelectedMicId(audioInputs[0].deviceId);
          console.log('[AUDIO] Using default microphone:', audioInputs[0].deviceId);
        }

        // Load saved speaker preference or use default
        const savedSpeakerId = localStorage.getItem('preferred_speaker');
        if (savedSpeakerId && audioOutputs.find(d => d.deviceId === savedSpeakerId)) {
          setSelectedSpeakerId(savedSpeakerId);
          console.log('[AUDIO] Loaded saved speaker preference:', savedSpeakerId);
        } else if (audioOutputs.length > 0) {
          setSelectedSpeakerId(audioOutputs[0].deviceId);
          console.log('[AUDIO] Using default speaker:', audioOutputs[0].deviceId);
        }
      } catch (error) {
        console.error('[AUDIO] Failed to enumerate devices:', error);
        toast.error('Failed to access microphone. Please grant permission.');
      }
    };

    enumerateDevices();
  }, []);

  // Start recording both audio streams
  const startRecording = (localStream: MediaStream, remoteStream: MediaStream) => {
    try {
      const ctx = new AudioContext();
      audioContextRef.current = ctx;

      // Create a destination to capture the mixed audio
      const dest = ctx.createMediaStreamDestination();

      // Mix local (mic) and remote (speaker) into one stream
      if (localStream.getAudioTracks().length > 0) {
        const localSource = ctx.createMediaStreamSource(localStream);
        localSource.connect(dest);
      }
      if (remoteStream.getAudioTracks().length > 0) {
        const remoteSource = ctx.createMediaStreamSource(remoteStream);
        remoteSource.connect(dest);
      }

      audioChunksRef.current = [];
      const recorder = new MediaRecorder(dest.stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.start(1000); // Collect data every second
      mediaRecorderRef.current = recorder;
      console.log('[RECORDING] Started recording call audio');
    } catch (err) {
      console.error('[RECORDING] Failed to start recording:', err);
    }
  };

  // Finalize recording: stop recorder and save blob before streams are destroyed
  const finalizeRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    // If recorder is still recording, stop it and collect chunks
    if (recorder.state === 'recording') {
      recorder.stop();
    }

    // Build blob from whatever chunks we have (collected every 1s via ondataavailable)
    if (audioChunksRef.current.length > 0) {
      const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      if (blob.size > 1000) {
        pendingBlobRef.current = blob;
        console.log('[RECORDING] Finalized recording: %d bytes', blob.size);
      }
    }

    audioChunksRef.current = [];
    audioContextRef.current?.close();
    audioContextRef.current = null;
    mediaRecorderRef.current = null;
  };

  // Upload recording for transcription
  const uploadRecording = async (callId: string) => {
    const blob = pendingBlobRef.current;
    pendingBlobRef.current = null;

    if (!blob) {
      console.log('[RECORDING] No recording blob to upload');
      return;
    }

    setIsTranscribing(true);
    try {
      console.log('[RECORDING] Uploading %d bytes for transcription', blob.size);
      const result = await api.uploadCallRecording({ call_id: callId, audioBlob: blob });
      if (result.status === 'completed') {
        toast.success('Call transcribed');
      } else {
        toast.info('Transcription pending');
      }
    } catch (err) {
      console.error('[RECORDING] Upload failed:', err);
    } finally {
      setIsTranscribing(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      finalizeRecording();
      if (telnyxClientRef.current) {
        telnyxClientRef.current.disconnect();
      }
    };
  }, []);

  // Note: Stream connection now handled in onCallUpdated callback
  // to avoid React useEffect reference equality issues

  // Handle microphone selection change
  const handleMicrophoneChange = (deviceId: string) => {
    setSelectedMicId(deviceId);
    localStorage.setItem('preferred_microphone', deviceId);
    console.log('[AUDIO] Microphone preference saved:', deviceId);

    // Apply to current Telnyx client if connected
    if (telnyxClientRef.current) {
      telnyxClientRef.current.setMicrophone(deviceId);
      toast.success('Microphone changed');
    }
  };

  // Handle speaker selection change
  const handleSpeakerChange = (deviceId: string) => {
    setSelectedSpeakerId(deviceId);
    localStorage.setItem('preferred_speaker', deviceId);
    console.log('[AUDIO] Speaker preference saved:', deviceId);

    // Apply to current Telnyx client if connected
    if (telnyxClientRef.current) {
      telnyxClientRef.current.setSpeaker(deviceId);
      toast.success('Speaker changed');
    }
  };

  // Format phone number for E.164
  const formatPhoneNumber = (input: string): string => {
    // Remove all non-digits
    const digits = input.replace(/\D/g, '');

    // If starts with 1 (North America), ensure it starts with +1
    if (digits.startsWith('1') && digits.length === 11) {
      return `+${digits}`;
    }

    // If 10 digits, assume North America
    if (digits.length === 10) {
      return `+1${digits}`;
    }

    // Otherwise, just add + if not already there
    return digits.startsWith('+') ? digits : `+${digits}`;
  };

  const handleInitiateCall = async () => {
    try {
      setIsConnecting(true);
      setError(null);

      // Format phone number
      const formattedNumber = formatPhoneNumber(phoneNumber);

      // Get available phone numbers
      const phoneNumbers = await api.listPhoneNumbers();
      if (phoneNumbers.length === 0) {
        throw new Error('No phone numbers available. Please contact administrator.');
      }

      // Use first available phone number (least used)
      const fromNumber = phoneNumbers[0];

      // Initiate call via backend
      const callResponse = await api.initiateCall({
        to_phone: formattedNumber,
        from_number_id: fromNumber.id,
        prospect_name: prospectName || '',
        prospect_id: prospectId,
      });

      setCurrentCallId(callResponse.call_id);
      setFromNumberDisplay(callResponse.from_number);

      // Create a promise that resolves when Telnyx client is ready
      let resolveReady: (() => void) | null = null;
      const clientReadyPromise = new Promise<void>((resolve) => {
        resolveReady = resolve;
      });

      // Connect to Telnyx
      const telnyxClient = new TelnyxClient({
        onStateChange: (state) => {
          setCallState(state);
          if (state === 'active') {
            toast.success('Call connected!');
          } else if (state === 'done' || state === 'purge' || state === 'destroy') {
            toast.info('Call ended');
            // Finalize recording NOW before streams are destroyed
            finalizeRecording();
            // Don't clear currentCallId here - the useEffect needs it to trigger
            // recording upload and outcome modal. It gets cleared after modal submit.
            setCurrentCall(null);
          }
        },
        onCallUpdated: (call) => {
          console.log('[AUDIO] Call updated - state:', call.state);
          setCurrentCall(call);

          // Connect streams immediately when call becomes active
          if (call.state === 'active' || call.state === 'ringing' || call.state === 'trying') {
            console.log('[AUDIO] Call is active/ringing - connecting streams now');
            console.log('[AUDIO] Remote stream:', call.remoteStream);
            console.log('[AUDIO] Local stream:', call.localStream);

            if (call.remoteStream && remoteAudioRef.current) {
              remoteAudioRef.current.srcObject = call.remoteStream;
              console.log('[AUDIO] ✅ Remote stream connected');
            } else {
              console.log('[AUDIO] ❌ Remote stream not available:', {
                hasStream: !!call.remoteStream,
                hasRef: !!remoteAudioRef.current
              });
            }

            if (call.localStream && localAudioRef.current) {
              localAudioRef.current.srcObject = call.localStream;
              const audioTracks = call.localStream.getAudioTracks();
              console.log('[AUDIO] ✅ Local stream connected');
              console.log('[AUDIO] Local audio tracks:', audioTracks);
              if (audioTracks.length > 0) {
                console.log('[AUDIO] Local track details:', {
                  enabled: audioTracks[0].enabled,
                  muted: audioTracks[0].muted,
                  readyState: audioTracks[0].readyState,
                  label: audioTracks[0].label
                });
              }
            } else {
              console.log('[AUDIO] ❌ Local stream not available:', {
                hasStream: !!call.localStream,
                hasRef: !!localAudioRef.current
              });
            }

            // Start recording when call is active and both streams are available
            if (call.state === 'active' && call.localStream && call.remoteStream && !mediaRecorderRef.current) {
              startRecording(call.localStream, call.remoteStream);
            }
          }
        },
        onError: (err) => {
          setError(err.message);
          toast.error(`Call error: ${err.message}`);
        },
        onReady: () => {
          console.log('Telnyx client ready - now safe to make calls');
          // Resolve the ready promise
          if (resolveReady) resolveReady();
        },
      });

      telnyxClientRef.current = telnyxClient;

      // Connect to Telnyx with username/password and selected audio devices
      await telnyxClient.connectWithCredentials(
        callResponse.telnyx_username,
        callResponse.telnyx_password,
        selectedMicId,
        selectedSpeakerId
      );

      // CRITICAL: Wait for the client to be fully ready before making the call
      console.log('Waiting for Telnyx client to be ready...');
      await clientReadyPromise;
      console.log('Client ready! Making call now...');

      // Audio devices already set during connection initialization

      // Make the call
      await telnyxClient.makeCall(formattedNumber, callResponse.from_number);

      toast.success('Call initiated!');
    } catch (err: any) {
      console.error('Failed to initiate call:', err);
      setError(err.message || 'Failed to initiate call');
      toast.error(err.message || 'Failed to initiate call');
      setCallState('idle');
      setCurrentCallId(null);
      setFromNumberDisplay(null);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleHangup = () => {
    if (telnyxClientRef.current) {
      telnyxClientRef.current.hangup();
      // Note: State will be updated via onStateChange when notification fires
    }
  };

  const handleDTMF = (digit: string) => {
    if (telnyxClientRef.current) {
      telnyxClientRef.current.sendDTMF(digit);
      setDtmfDigits(prev => prev + digit);
    }
  };

  // Show outcome modal and upload recording when call ends
  useEffect(() => {
    if (callState === 'done' && currentCallId) {
      // Save the completed call ID before resetting
      setCompletedCallId(currentCallId);
      setShowOutcomeModal(true);
      setShowKeypad(false);
      setDtmfDigits('');

      // Upload recording for transcription (non-blocking)
      uploadRecording(currentCallId);
    }
  }, [callState, currentCallId]);

  const handleOutcomeSubmit = async (
    outcome: string,
    notes?: string,
    callbackDate?: Date
  ) => {
    if (!completedCallId) return;

    try {
      await updateCallOutcome.mutateAsync({
        callId: completedCallId,
        outcome,
        notes,
        callback_date: callbackDate?.toISOString(),
      });

      toast.success('Call outcome saved');

      // Reset call state
      setCurrentCallId(null);
      setFromNumberDisplay(null);
      setCompletedCallId(null);
      setCallState('idle');
    } catch (error) {
      console.error('Failed to save outcome:', error);
      toast.error('Failed to save call outcome');
      throw error; // Let modal handle error state
    }
  };

  const isOnCall = callState !== 'idle' && callState !== 'done' && callState !== 'purge' && callState !== 'destroy';
  const canCall = phoneNumber.length >= 10 && !isOnCall && !isConnecting;

  return (
    <>
      <CallOutcomeModal
        open={showOutcomeModal}
        onClose={() => {
          setShowOutcomeModal(false);
          setCurrentCallId(null);
      setFromNumberDisplay(null);
          setCompletedCallId(null);
          setCallState('idle');
        }}
        callId={completedCallId || ''}
        prospectName={prospectName}
        onSubmit={handleOutcomeSubmit}
      />

      <Card>
      {/* Audio elements for MediaStreams - connected programmatically like official Telnyx demo */}
      <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
      <audio ref={localAudioRef} autoPlay muted style={{ display: 'none' }} />

      <CardHeader>
        <CardTitle>Make a Call</CardTitle>
        <CardDescription>
          Enter a phone number to initiate an outbound call
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Audio Device Selection */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="microphone" className="text-sm font-medium">
              Microphone
            </label>
            <select
              id="microphone"
              value={selectedMicId}
              onChange={(e) => handleMicrophoneChange(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isOnCall}
            >
              {audioInputDevices.length === 0 ? (
                <option value="">Loading...</option>
              ) : (
                audioInputDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="speaker" className="text-sm font-medium">
              Speaker
            </label>
            <select
              id="speaker"
              value={selectedSpeakerId}
              onChange={(e) => handleSpeakerChange(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isOnCall}
            >
              {audioOutputDevices.length === 0 ? (
                <option value="">Loading...</option>
              ) : (
                audioOutputDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          Select audio devices before starting a call
        </p>

        <div className="space-y-2">
          <label htmlFor="phone" className="text-sm font-medium">
            Phone Number
          </label>
          <Input
            id="phone"
            type="tel"
            placeholder="+1 415 555 1234"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            disabled={isOnCall || isConnecting}
          />
          <p className="text-xs text-muted-foreground">
            Enter 10-digit number (e.g., 4155551234) or full E.164 format (+14155551234)
          </p>
        </div>

        <div className="flex items-center gap-3">
          {!isOnCall ? (
            <Button
              onClick={handleInitiateCall}
              disabled={!canCall}
              className="flex-1"
            >
              <Phone className="mr-2 h-4 w-4" />
              {isConnecting ? 'Connecting...' : 'Call'}
            </Button>
          ) : (
            <Button
              onClick={handleHangup}
              variant="destructive"
              className="flex-1"
            >
              <PhoneOff className="mr-2 h-4 w-4" />
              Hang Up
            </Button>
          )}
        </div>

        {callState !== 'idle' && (
          <div className="bg-muted p-4 rounded-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Call Status:</p>
                <p className="text-lg font-bold capitalize">{callState}</p>
              </div>
              {isOnCall && (
                <Button
                  variant={showKeypad ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setShowKeypad(!showKeypad); setDtmfDigits(''); }}
                >
                  <Grid3X3 className="mr-1 h-4 w-4" />
                  Keypad
                </Button>
              )}
            </div>
            {fromNumberDisplay && (
              <p className="text-sm font-mono font-semibold mt-2">
                Callback #: {fromNumberDisplay}
              </p>
            )}
            {currentCallId && (
              <p className="text-xs text-muted-foreground mt-1">
                Call ID: {currentCallId}
              </p>
            )}
          </div>
        )}

        {showKeypad && isOnCall && (
          <div className="bg-muted p-4 rounded-md space-y-3">
            {dtmfDigits && (
              <p className="text-center text-lg font-mono tracking-widest">{dtmfDigits}</p>
            )}
            <div className="grid grid-cols-3 gap-2 max-w-[200px] mx-auto">
              {['1','2','3','4','5','6','7','8','9','*','0','#'].map((digit) => (
                <Button
                  key={digit}
                  variant="outline"
                  className="h-12 text-lg font-semibold"
                  onClick={() => handleDTMF(digit)}
                >
                  {digit}
                </Button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
};
